import axios from 'axios'

import { internalApi } from '@/utils/httpclient'

import { sleep } from '../timers'

const BASE_URL = '/agent-builder'
const DEFAULT_WAIT = 25
const MIN_BACKOFF = 1000
const MAX_BACKOFF = 15000
const RESULT_RETRIES = 3
const HANDLED_LIMIT = 50

const remember = (handled, id, value) => {
  handled.set(id, value)
  while (handled.size > HANDLED_LIMIT) {
    handled.delete(handled.keys().next().value)
  }
}

export async function pair({ code, siteId, postId }) {
  const { data } = await internalApi.post(`${BASE_URL}/pair`, { code, siteId, postId })
  return data
}

export async function close(pairingId) {
  const { data } = await internalApi.post(`${BASE_URL}/pairings/${pairingId}/close`)
  return data
}

async function pollCommand({ pairingId, wait = DEFAULT_WAIT, signal }) {
  const { data } = await internalApi.get(`${BASE_URL}/commands`, {
    params: { pairingId, wait },
    signal,
    timeout: (wait + 10) * 1000,
  })
  return data
}

async function postResult(commandId, payload, signal) {
  const { data } = await internalApi.post(`${BASE_URL}/commands/${commandId}/result`, payload, { signal })
  return data
}

async function postResultWithRetry(commandId, payload, signal) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await postResult(commandId, payload, signal)
    } catch (error) {
      const status = error?.response?.status
      if (signal?.aborted || status === 409 || status === 404 || status === 410 || attempt >= RESULT_RETRIES) {
        throw error
      }
      await sleep(MIN_BACKOFF * attempt, signal)
    }
  }
}

const isGone = status => status === 404 || status === 410
const isUnauthorized = status => status === 401 || status === 403

export function createLongPollingTransport({ pairingId, wait = DEFAULT_WAIT }) {
  const handled = new Map()

  const run = async ({ onCommand, onStatus, signal }) => {
    let backoff = MIN_BACKOFF

    while (!signal.aborted) {
      try {
        const { command } = await pollCommand({ pairingId, wait, signal })
        backoff = MIN_BACKOFF

        if (!command) {
          continue
        }

        onStatus('executing', { command })

        let result = handled.get(command.id)
        if (!result) {
          result = await onCommand(command)
          remember(handled, command.id, result)
        }

        await postResultWithRetry(command.id, result, signal)
        onStatus('connected')
      } catch (error) {
        if (signal.aborted || axios.isCancel(error)) {
          return
        }

        const status = error?.response?.status

        if (isGone(status)) {
          onStatus('disconnected', { reason: 'pairing-gone', error })
          return
        }

        if (isUnauthorized(status)) {
          onStatus('error', { reason: 'unauthorized', error })
          return
        }

        onStatus('reconnecting', { error })
        await sleep(backoff, signal)
        backoff = Math.min(backoff * 2, MAX_BACKOFF)
      }
    }
  }

  return { run }
}
